import { useEffect } from "react";
import confetti from "canvas-confetti";
import { NotificationNudgeModal } from "@/components/NotificationNudgeModal";
import { EmptyState } from "@/components/shared/EmptyState";
import { MEDIA_QUERIES } from "@/constants/layout";
import { HomePages } from "@/features/navigation/HomePages";
import { PAGE_INFO } from "@/features/navigation/pages";
import { useHomeLayout } from "@/features/navigation/useHomeLayout";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useRecentStashes } from "@/hooks/useRecentStashes";
import { MAP } from "@/routes";
import StashesFeed from "./StashesFeed";

const FIRST_STASH_RECENCY_MS = 300_000; // 5 min — generous for pipeline latency

/**
 * Home (native StashesPage.swift): the pages placed on Home (Shortcuts, which the menu
 * leads) and Recent Stashes, the account's stashes newest first under
 * the tag row, which is the All stashes feed itself. The shortcuts sit beside it at
 * desktop and lead it otherwise.
 */
const Home = () => {
  const isDesktop = useMediaQuery(MEDIA_QUERIES.desktop);
  const hasShortcuts = useHomeLayout().layout.home.some(page => PAGE_INFO[page] !== null);
  // The feed's own unfiltered query, so reading it here costs no request.
  const { data: recentPosts, total: recentTotal, isLoading: recentLoading } = useRecentStashes();

  // First stash confetti: celebrate when the user saves their very first item.
  // Gates: server total is exactly 1, the stash was created in the last 5 min
  // (rules out old users with a single stash), and we haven't celebrated before.
  useEffect(() => {
    if (recentLoading || recentTotal !== 1) return;
    const firstItem = recentPosts?.[0];
    if (!firstItem?.createdAt) return;
    const age = Date.now() - new Date(firstItem.createdAt).getTime();
    if (Number.isNaN(age) || age > FIRST_STASH_RECENCY_MS) return;
    try {
      if (localStorage.getItem("stasht_first_stash_celebrated") === "true") return;
      localStorage.setItem("stasht_first_stash_celebrated", "true");
      const shoot = () => {
        confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 } });
        confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0 } });
        confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1 } });
      };
      shoot();
      setTimeout(shoot, 400);
    } catch (e) {
      console.warn("confetti failed", e);
    }
  }, [recentLoading, recentTotal, recentPosts]);

  return (
    <>
      <NotificationNudgeModal enabled={(recentPosts?.length ?? 0) > 0} />
      <StashesFeed
        tabId="home"
        title="Home"
        sectionTitle="Recent Stashes"
        scrollContent
        defaultTagScope="mine"
        aside={hasShortcuts ? <HomePages pane={isDesktop} className="px-0 md:px-0" /> : undefined}
        emptyState={
          <EmptyState
            headline="Now the fun begins!"
            message="Send Stasht restaurants, events, travel, recipes, gift ideas from any app and watch your homepage come together."
            variant="full"
            showSetupCards
            showWaysToSave
            showActionButtons
            mapPrompt={{
              headline: "See what's already worth saving nearby.",
              ctaLabel: "Explore nearby stashes!",
              to: `${MAP()}?nearby=1`,
            }}
          />
        }
      />
    </>
  );
};

export default Home;
